import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Flag, Loader2, Plus, ArrowRight, Clock, ShieldCheck, Trash2, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const money = (p) => `£${(p / 100).toFixed(2)}`;

const PACKS = [
  { lookup: "race_pack_oneoff", price: 999, name: "Race Pack",
    blurb: "One race — race week + race day fuelling. Credit valid 12 months." },
  { lookup: "race_pack_annual", price: 1999, name: "Race Season",
    blurb: "Unlimited race plans for 12 months." },
];

// Home for one-off Race Pack buyers: entitlement, their race plans and the
// entry point into the 3-step race setup. No subscription required.
export default function RacePack() {
  const navigate = useNavigate();
  const { logout, refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [buying, setBuying] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/race-pack");
      setState(data);
    } catch (e) {
      toast.error(apiError(e, "Could not load your race packs."));
      setState({ kind: "none", credits: 0, canBuild: false, plans: [] });
    }
  };

  useEffect(() => { load(); refreshUser?.(); /* eslint-disable-next-line */ }, []);

  const buy = async (lookup) => {
    setBuying(lookup);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup, origin_url: window.location.origin });
      if (data?.error || !data?.checkout_url) {
        toast.error(data?.error || "Could not start checkout.");
        setBuying("");
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Could not start checkout."));
      setBuying("");
    }
  };

  const removePlan = async (p) => {
    if (!window.confirm(`Delete "${p.name || "this race plan"}"? This cannot be undone and your race credit isn't refunded.`)) return;
    try {
      await api.delete(`/race-pack/plans/${p.id}`);
      toast.success("Race plan deleted");
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not delete that race plan."));
    }
  };

  if (!state) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" />
      </div>
    );
  }

  const open = (state.plans || []).filter((p) => !p.expired);
  const past = (state.plans || []).filter((p) => p.expired);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-20" data-testid="race-pack-page">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9" />
            <span className="font-display text-base uppercase tracking-tight">
              <span className="text-[#E2E30A]">Race</span> Fuelling
            </span>
          </Link>
          <button data-testid="race-pack-logout" onClick={logout}
            className="text-[11px] uppercase tracking-wider text-zinc-400 hover:text-white">Sign out</button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 pt-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">Race Pack</p>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter font-black mt-2">
          Your race fuelling
        </h1>
        <p className="text-sm text-zinc-300 mt-3 max-w-lg">
          Race week + race day only. No subscription, nothing to cancel.
        </p>

        {/* Entitlement strip */}
        <div className="mt-8 border border-white/10 bg-white/[0.02] p-5" data-testid="race-pack-state">
          {state.kind === "annual" ? (
            <p className="text-sm text-white">
              <span className="text-[#E2E30A] font-bold uppercase tracking-wider text-xs">Race Season active</span>
              <br />Unlimited race plans until {String(state.unlimitedUntil || "").slice(0, 10)}.
            </p>
          ) : state.credits > 0 ? (
            <p className="text-sm text-white">
              <span className="text-[#E2E30A] font-bold uppercase tracking-wider text-xs">
                {state.credits} race {state.credits === 1 ? "credit" : "credits"} ready
              </span>
              <br />Use by {String(state.creditExpiresAt || "").slice(0, 10)}.
            </p>
          ) : (
            <p className="text-sm text-zinc-300">
              No race credits left. Grab a pack below to build your next race plan.
            </p>
          )}
        </div>

        {state.canBuild && (
          <button
            type="button"
            data-testid="race-pack-build-btn"
            onClick={() => navigate("/race-pack/new")}
            className="mt-4 w-full inline-flex items-center justify-between gap-3 bg-[#E2E30A] hover:bg-white text-black font-display font-black uppercase tracking-wider px-5 py-4 transition-colors"
          >
            <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Build my race plan</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        )}

        {/* Plans */}
        {!!open.length && (
          <section className="mt-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-bold">Your race plans</p>
            <div className="mt-3 space-y-2">
              {open.map((p) => (
                <div key={p.id} className="flex items-center gap-2 border border-white/12 bg-white/[0.02] hover:bg-white/[0.05] transition-colors">
                  <button type="button" data-testid={`race-plan-${p.id}`}
                    onClick={() => navigate(`/race-pack/${p.id}`)}
                    className="flex-1 min-w-0 text-left flex items-center gap-4 p-4">
                    <span className="grid place-items-center h-10 w-10 shrink-0 bg-[#E2E30A]/10 border border-[#E2E30A]/40">
                      <Flag className="h-4 w-4 text-[#E2E30A]" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-display text-lg uppercase tracking-tight truncate">{p.name}</span>
                      <span className="block text-xs text-zinc-400 mt-0.5">Race day {p.raceDate}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-zinc-400 shrink-0" />
                  </button>
                  <button type="button" data-testid={`race-plan-edit-${p.id}`}
                    onClick={() => navigate(`/race-pack/${p.id}/edit`)} aria-label={`Edit ${p.name}`}
                    className="shrink-0 grid place-items-center h-10 w-10 text-zinc-400 hover:text-[#E2E30A] hover:bg-[#E2E30A]/10 transition-colors">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`race-plan-delete-${p.id}`}
                    onClick={() => removePlan(p)} aria-label={`Delete ${p.name}`}
                    className="shrink-0 grid place-items-center h-10 w-10 mr-3 text-zinc-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {!!past.length && (
          <section className="mt-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">Closed</p>
            <div className="mt-3 space-y-2">
              {past.map((p) => (
                <div key={p.id} className="flex items-center gap-3 border border-white/8 p-4 text-zinc-500">
                  <Clock className="h-4 w-4 shrink-0" />
                  <span className="text-sm flex-1 min-w-0">{p.name} · race day {p.raceDate} · closed 7 days after race day</span>
                  <button type="button" data-testid={`race-plan-delete-${p.id}`}
                    onClick={() => removePlan(p)} aria-label={`Delete ${p.name}`}
                    className="shrink-0 grid place-items-center h-8 w-8 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Buy more */}
        <section className="mt-12">
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-bold">
            {state.canBuild ? "Need another race?" : "Get your race plan"}
          </p>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {PACKS.map((p) => (
              <button key={p.lookup} type="button" data-testid={`buy-${p.lookup}`}
                onClick={() => buy(p.lookup)} disabled={!!buying}
                className="text-left border border-white/14 hover:border-[#E2E30A]/60 bg-white/[0.02] hover:bg-[#E2E30A]/[0.04] p-5 transition-colors disabled:opacity-60">
                <p className="font-display text-xl uppercase tracking-tight">{p.name}</p>
                <p className="font-display text-4xl font-black tabular-nums mt-2">{money(p.price)}
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">one-off</span></p>
                <p className="text-xs text-zinc-400 mt-2">{p.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#E2E30A] font-bold">
                  {buying === p.lookup ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Buy now
                </span>
              </button>
            ))}
          </div>
          <p className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 mt-4">
            <ShieldCheck className="h-3 w-3" /> One-off payment · no subscription · secured by Stripe
          </p>
          <p className="text-xs text-zinc-400 mt-6">
            Want weekly meal plans and the food tracker too?{" "}
            <Link to="/subscribe" className="text-[#E2E30A] underline hover:no-underline">See the full plan</Link>
          </p>
        </section>
      </main>
    </div>
  );
}
